import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { LOG_LEVELS, type LogEntry, type LogLevel, type LogQueryResult } from '@fame/shared';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

const RANGE_PRESETS = [
  { durationMs: 15 * 60_000, label: '15 min' },
  { durationMs: 60 * 60_000, label: '1 uur' },
  { durationMs: 6 * 60 * 60_000, label: '6 uur' },
  { durationMs: 24 * 60 * 60_000, label: '24 uur' },
  { durationMs: 7 * 24 * 60 * 60_000, label: '7 dagen' },
];

const levelClassNames: Record<LogLevel, string> = {
  trace: 'bg-slate-500',
  debug: 'bg-sky-700',
  info: 'bg-emerald-700',
  warn: 'bg-amber-600',
  error: 'bg-red-700',
  fatal: 'bg-red-950',
  unknown: 'bg-slate-500',
};

export function LogsPage() {
  const [range, setRange] = useState(() => createRange(60 * 60_000));
  const [level, setLevel] = useState<LogLevel | 'all'>('all');
  const [pod, setPod] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<LogQueryResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const requestId = useRef(0);

  const loadLogs = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      start: new Date(range.start).toISOString(),
      end: new Date(range.end).toISOString(),
    });
    if (level !== 'all') params.set('level', level);
    if (pod !== 'all') params.set('pod', pod);
    if (search.trim()) params.set('search', search.trim());

    try {
      const result = await api.get<LogQueryResult>(`/logs?${params}`);
      if (currentRequest === requestId.current) setData(result);
    } catch (cause) {
      if (currentRequest === requestId.current) {
        setError(cause instanceof Error ? cause.message : 'Logs konden niet worden geladen');
      }
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [level, pod, range.end, range.start, search]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- Starts the network request whenever the active filters change.
    void loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = window.setInterval(() => {
      const durationMs = Date.parse(range.end) - Date.parse(range.start);
      setRange(createRange(durationMs));
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, range.end, range.start]);

  const copyEntry = async (entry: LogEntry) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(entry, null, 2));
    } catch {
      setError('Logregel kon niet naar het klembord worden gekopieerd');
    }
  };

  const downloadEntries = () => {
    const blob = new Blob([JSON.stringify(data?.entries ?? [], null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fame-backoffice-logs-${new Date().toISOString().replace(/:/g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Applicatielogs</h1>
        <p className="text-muted-foreground">
          {data?.source === 'fixture'
            ? 'Lokale fixturedata; configureer Loki om gedeployde logs te bekijken.'
            : 'Doorzoekbare Loki-loghistoriek van de FAME Backoffice-applicatie.'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Logfilters</CardTitle>
          <CardDescription>
            Maximaal zeven dagen loggeschiedenis, met ten hoogste 500 regels per aanvraag.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Van</span>
              <Input
                type="datetime-local"
                value={range.start}
                max={range.end}
                onChange={event => setRange(current => ({ ...current, start: event.target.value }))}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tot</span>
              <Input
                type="datetime-local"
                value={range.end}
                min={range.start}
                max={toDateTimeLocal(new Date())}
                onChange={event => setRange(current => ({ ...current, end: event.target.value }))}
              />
            </label>
            <LogSelect label="Niveau" value={level} onChange={value => setLevel(value as LogLevel | 'all')}>
              <option value="all">Alle niveaus</option>
              {LOG_LEVELS.filter(value => value !== 'unknown').map(value => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </LogSelect>
            <LogSelect label="Pod" value={pod} onChange={setPod}>
              <option value="all">Alle pods</option>
              {(data?.pods ?? []).map(value => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </LogSelect>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Zoeken</span>
              <Input
                value={search}
                onChange={event => setSearch(event.target.value)}
                onKeyDown={event => event.key === 'Enter' && void loadLogs()}
                placeholder="Bijv. webhook, timeout..."
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground">Snel:</span>
            {RANGE_PRESETS.map(preset => (
              <Button key={preset.label} variant="outline" onClick={() => setRange(createRange(preset.durationMs))}>
                {preset.label}
              </Button>
            ))}
            <Button onClick={() => void loadLogs()} disabled={loading}>
              {loading ? 'Laden...' : 'Vernieuwen'}
            </Button>
            <Button variant="outline" onClick={downloadEntries} disabled={!data?.entries.length}>
              Download JSON
            </Button>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={autoRefresh} onChange={event => setAutoRefresh(event.target.checked)} />
              Rollend venster vernieuwen (15 sec)
            </label>
            <span className="text-sm text-muted-foreground">
              {data ? `${data.entries.length} regels · bijgewerkt ${formatTime(data.queriedAt)}` : ''}
            </span>
            {error && <span className="text-sm text-destructive">{error}</span>}
          </div>
        </CardContent>
      </Card>

      <div className="max-h-[65vh] overflow-auto rounded-md border bg-muted/30 font-mono text-xs">
        {!data?.entries.length ? (
          <div className="p-8 text-center font-sans text-sm text-muted-foreground">
            {loading ? 'Logs laden...' : 'Geen logs gevonden voor deze filters.'}
          </div>
        ) : (
          <div className="divide-y">
            {data.entries.map((entry, index) => (
              <LogRow
                entry={entry}
                key={`${entry.timestamp}-${entry.pod}-${index}`}
                onCopy={() => void copyEntry(entry)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LogSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="space-y-1">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {children}
      </select>
    </label>
  );
}

function LogRow({ entry, onCopy }: { entry: LogEntry; onCopy: () => void }) {
  return (
    <article className="group grid gap-2 p-3 hover:bg-muted/60 md:grid-cols-[11rem_7rem_1fr_auto] md:items-start">
      <time className="text-muted-foreground">{formatTime(entry.timestamp)}</time>
      <div className="space-y-1">
        <Badge className={`${levelClassNames[entry.level]} pointer-events-none uppercase`}>{entry.level}</Badge>
        <div className="truncate text-muted-foreground" title={`${entry.pod}/${entry.container}`}>
          {entry.pod}
        </div>
      </div>
      <div className="min-w-0">
        <p className="whitespace-pre-wrap break-words">{entry.message}</p>
        {entry.details && (
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-muted-foreground">
            {JSON.stringify(entry.details, null, 2)}
          </pre>
        )}
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="rounded px-2 py-1 font-sans text-muted-foreground hover:bg-muted hover:text-foreground md:opacity-0 md:group-hover:opacity-100"
      >
        Kopieer
      </button>
    </article>
  );
}

function formatTime(value: string) {
  return new Date(value).toLocaleString('nl-BE', { hour12: false });
}

function createRange(durationMs: number) {
  const end = new Date();
  return { start: toDateTimeLocal(new Date(end.getTime() - durationMs)), end: toDateTimeLocal(end) };
}

function toDateTimeLocal(value: Date) {
  return new Date(value.getTime() - value.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}
